import { useState, useRef, useEffect, useCallback, memo } from 'react';

export interface DropdownOption<T = any> {
  label: string;
  value: T;
}

interface DropdownProps<T = any> {
  options: DropdownOption<T>[];
  value: T;
  onChange: (value: T) => void;
  placeholder?: string;
  label?: string;
  className?: string;
  buttonClassName?: string;
  optionsClassNames?: string;
  labelClassName?: string;
}

function Dropdown<T>({
  options,
  value,
  onChange,
  placeholder = 'Select option',
  label,
  className = '',
  buttonClassName = '',
  labelClassName = '',
  optionsClassNames = '',
}: DropdownProps<T>) {
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement | null>(null);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const selectedLabel = options.find((opt) => opt.value === value)?.label;

  const handleSelect = useCallback(
    (val: T) => {
      onChange(val);
      setOpen(false);
    },
    [onChange],
  );

  return (
    <div
      ref={dropdownRef}
      className={`relative inline-block w-full ${className}`}
    >
      {/* Optional Label */}
      {label && (
        <label
          className={`block font-medium text-gray-700 high-contrast:text-gray-900 mb-2 ${labelClassName}`}
        >
          {label}
        </label>
      )}

      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className={`flex items-center justify-between gap-2 p-3 bg-white border border-gray-300 rounded-md shadow-sm min-w-[200px] ${buttonClassName}`}
      >
        <span className={`text-sm md:text-base font-medium text-text-secondary`}>
          {selectedLabel || placeholder}
        </span>
        <svg
          className={`w-4 h-4 transition-transform duration-200 ${
            open ? 'rotate-180' : 'rotate-0'
          }`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>

      {open && (
        <div className="absolute left-0 mt-2 w-full bg-white border border-gray-200 rounded-md shadow-lg z-50 animate-fade-in">
          {options.map((opt) => (
            <div
              key={String(opt.value)}
              onClick={() => handleSelect(opt.value)}
              className={`px-3 py-2 cursor-pointer hover:bg-gray-100 text-sm md:text-base ${optionsClassNames} ${
                value === opt.value ? 'bg-gray-50 font-semibold' : ''
              }`}
            >
              {opt.label}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default memo(Dropdown);
